import React, {
  ReactElement,
  createRef,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Debug from "debug";
import videojs, { VideoJsPlayer } from "video.js";
import "./VideoPlayer.scss";
import { StreamState, PlayerState, AuthMode, ViewerStore } from "../../types";
import { RootState } from "../../reducers";
import { addReaction, reactToStream } from "../../actions";
import { Loader } from "@screencloud/screencloud-ui-components";
import Reaction from "../Reaction/Reaction";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import useShowWhenActive from "../../hooks/useShowWhenActive";

const debug = Debug("broadcast:components:VideoPlayer");

interface VideoPlayerProps {
  url: string;
  showControls: boolean;
  onStreamStateChange: (state: StreamState) => void;
}

const VideoPlayer = (
  props: VideoPlayerProps
): ReactElement<VideoPlayerProps> => {
  const dispatch = useDispatch();
  const viewer: ViewerStore = useSelector((state: RootState) => state.viewer);

  const { url, onStreamStateChange, showControls } = props;
  const videoElement = createRef<HTMLVideoElement>();
  const [player, setPlayer] = useState<VideoJsPlayer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const emojiPickerRef = useRef<HTMLDivElement>(null);
  useShowWhenActive(emojiPickerRef, isFullScreen);

  const onReaction = useCallback(
    (cue: { text: string; startTime: number; endTime: number }) => {
      const reactionJson = {
        ...JSON.parse(cue.text),
        timestamp: new Date(),
        startTime: cue.startTime,
        endTime: cue.endTime,
      };

      dispatch(addReaction(reactionJson));
    },
    [dispatch]
  );

  useEffect(() => {
    if (videoElement.current) {
      /* eslint-disable */
      // @ts-ignore
      registerIVSTech(videojs);
      // @ts-ignore
      registerIVSQualityPlugin(videojs);

      const videoJsPlayer = videojs(
        videoElement.current,
        {
          techOrder: ["AmazonIVS"],
          controls: showControls,
          fluid: true,
        },
        function onPlayerReady() {
          // @ts-ignore
          this.play();
        }
      );
      // @ts-ignore
      videoJsPlayer.enableIVSQualityPlugin();

      let fullUrl = url;
      if (viewer.channel !== null) {
        if (
          viewer.channel.authMode !== AuthMode.Public &&
          viewer.channel.authMode !== AuthMode.Private &&
          viewer.token !== null
        ) {
          fullUrl = `${url}?token=${viewer.token}`;
        }
        debug("FULL URL", fullUrl);
        videoJsPlayer.src(fullUrl);
        videoJsPlayer.on("fullscreenchange", () => {
          setIsFullScreen(videoJsPlayer.isFullscreen());
        });
        setPlayer(videoJsPlayer);
        // eslint-disable-next-line
        const ivsPlayer = (videoJsPlayer as any).getIVSPlayer();
        // eslint-disable-next-line
        const playerEvent = (videoJsPlayer as any).getIVSEvents()
          .PlayerEventType;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ivsPlayer.addEventListener(playerEvent.ERROR, (error: any) => {
          debug("Error is", error);
          const { type: errorType } = error;
          if (errorType === "ErrorAuthorization") {
            onStreamStateChange(StreamState.AuthError);
          } else {
            onStreamStateChange(StreamState.Offline);
          }
        });
        ivsPlayer.addEventListener(playerEvent.TEXT_METADATA_CUE, onReaction);
        const playerStateCheck = setInterval(() => {
          if (ivsPlayer) {
            const state = ivsPlayer.getState();
            if (
              state === PlayerState.Playing ||
              state === PlayerState.Buffering ||
              state === PlayerState.Idle
            ) {
              setIsInitialized(true);
            }
            if (state === PlayerState.Ended) {
              onStreamStateChange(StreamState.Offline);
            }
          }
        }, 2000);

        return () => clearInterval(playerStateCheck);
      }
    }

    return () => {
      if (player) {
        player.dispose();
      }
    };
  }, []);

  return (
    <>
      <div className={`video-js-player ${showControls ? "" : "fullscreen"}`}>
        <div data-vjs-player>
          <video ref={videoElement} className="video-js" autoPlay />
          {isFullScreen && (
            <div className="fullscreen-emoji-picker">
              <EmojiPicker
                ref={emojiPickerRef}
                mode="vertical"
                onEmojiPicked={(e) => {
                  if (viewer.channel !== null) {
                    const { screenCloudOrgId, spaceId, channelId, arn } =
                      viewer.channel;

                    dispatch(
                      reactToStream({
                        orgId: screenCloudOrgId,
                        spaceId,
                        channelId,
                        arn,
                        reactionData: {
                          userName:
                            viewer && viewer.user
                              ? viewer.user.name
                              : "Someone",
                          reaction: e.value,
                          profilePictureUrl:
                            viewer && viewer.user
                              ? viewer.user.profilePictureUrl
                              : null,
                        },
                      })
                    );
                  }
                }}
              />
            </div>
          )}
          {!!viewer.reactions && !!viewer.reactions.length && (
            <div
              className={`reactions-container  ${
                isFullScreen ? "fullscreen" : ""
              }`}
            >
              <div className="reactions">
                {viewer.reactions.map((reaction, index) => {
                  return (
                    <Reaction
                      key={index}
                      reaction={reaction.reaction}
                      timestamp={reaction.timestamp}
                      userName={reaction.userName}
                      profilePictureUrl={reaction.profilePictureUrl}
                      description={reaction.description}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {!isInitialized && (
        <div className="player-loader-overlay">
          <Loader
            inline="centered"
            size="massive"
            active
            className="player-loader"
          />
        </div>
      )}
    </>
  );
};

export default VideoPlayer;
